import { NavLink } from "react-router-dom";
import React, { useMemo, useState } from "react";

import "../style/automationTesting.css";

import TextBoxForm from "../pages/automation_practice/TextFormBox";
import CheckBox from "../pages/automation_practice/CheckBox";
import RadioButton from "../pages/automation_practice/RadioButton";
import WebTables from "../pages/automation_practice/WebTables.jsx";
import Buttons from "../pages/automation_practice/Buttons.jsx";
import Links from "../pages/automation_practice/Links.jsx";
import BrokenLinks from "../pages/automation_practice/BrokenLinks.jsx";
import UploadDownload from "../pages/automation_practice/UploadDownload.jsx";
import DynamicProperties from "../pages/automation_practice/DynamicProperties.jsx";
import PracticeForm from "../pages/automation_practice/PracticeForm.jsx";
import Login from "../pages/automation_practice/Login.jsx";
import Register from "../pages/automation_practice/Register.jsx";
import BrowserWindows from "../pages/automation_practice/BrowserWindows.jsx";
import Alerts from "../pages/automation_practice/Alerts.jsx";
import Frames from "../pages/automation_practice/Frames.jsx";
import NestedFrames from "../pages/automation_practice/NestedFrames.jsx";
import ModalDialogs from "../pages/automation_practice/ModalDialogs.jsx";
import Accordion from "../pages/automation_practice/Accordion.jsx";
import AutoComplete from "../pages/automation_practice/AutoComplete.jsx";
import DatePicker from "../pages/automation_practice/DatePicker.jsx";
import Slider from "../pages/automation_practice/Slider.jsx";
import ProgressBar from "../pages/automation_practice/ProgressBar.jsx";
import Tabs from "../pages/automation_practice/Tabs.jsx";
import ToolTips from "../pages/automation_practice/ToolTips.jsx";
import Menu from "../pages/automation_practice/Menu.jsx";
import SelectMenu from "../pages/automation_practice/SelectMenu.jsx";
import ScrollTop from "../pages/automation_practice/ScrollTop.jsx";
import ScrollDown from "../pages/automation_practice/ScrollDown.jsx";
import HorizontalScroll from "../pages/automation_practice/HorizontalScroll.jsx";
import Sortable from "../pages/automation_practice/Sortable.jsx";
import Selectable from "../pages/automation_practice/Selectable.jsx";
import Resizable from "../pages/automation_practice/Resizable.jsx";
import Droppable from "../pages/automation_practice/Droppable.jsx";
import Draggable from "../pages/automation_practice/Draggable.jsx";
import EcommerceAutomationLab from "../components/EcommerceAutomationLab.jsx";

const STORAGE_KEY = "qaCoderAutomationPracticeProgress";

const menuData = [
  {
    id: "elements",
    title: "Elements",
    description: "Foundational UI controls for locators, inputs, tables, and validation practice.",
    items: [
      "Text Box",
      "Check Box",
      "Radio Button",
      "Web Tables",
      "Buttons",
      "Links",
      "Broken Links",
      "Upload Download",
      "Dynamic Properties",
    ],
  },
  {
    id: "forms",
    title: "Forms",
    description: "Structured form flows for submission, validation, and auth journey practice.",
    items: ["Practice Form", "Login", "Register"],
  },
  {
    id: "alerts",
    title: "Alerts, Frames & Windows",
    description: "Browser popups, frame switching, and dialog handling scenarios.",
    items: ["Browser Windows", "Alerts", "Frames", "Nested Frames", "Modal Dialogs"],
  },
  {
    id: "widgets",
    title: "Widgets",
    description: "Interactive UI controls that mimic advanced user journeys and stateful inputs.",
    items: [
      "Accordion",
      "Auto Complete",
      "Date Picker",
      "Slider",
      "Progress Bar",
      "Tabs",
      "Tool Tips",
      "Menu",
      "Select Menu",
      "Scroll Top",
      "Scroll Down",
      "Horizontal Scroll",
    ],
  },
  {
    id: "interaction",
    title: "Interaction",
    description: "High-signal drag, drop, resize, sort, and selection exercises.",
    items: ["Sortable", "Selectable", "Resizable", "Droppable", "Draggable"],
  },
  {
    id: "ecommerce",
    title: "Ecommerce Flow",
    description: "A complete storefront-to-order journey for realistic automation scenarios.",
    items: ["Ecommerce Lab"],
  },
];

const componentMap = {
  "Text Box": <TextBoxForm />,
  "Check Box": <CheckBox />,
  "Radio Button": <RadioButton />,
  "Web Tables": <WebTables />,
  Buttons: <Buttons />,
  Links: <Links />,
  "Broken Links": <BrokenLinks />,
  "Upload Download": <UploadDownload />,
  "Dynamic Properties": <DynamicProperties />,
  "Practice Form": <PracticeForm />,
  Login: <Login />,
  Register: <Register />,
  "Browser Windows": <BrowserWindows />,
  Alerts: <Alerts />,
  Frames: <Frames />,
  "Nested Frames": <NestedFrames />,
  "Modal Dialogs": <ModalDialogs />,
  Accordion: <Accordion />,
  "Auto Complete": <AutoComplete />,
  "Date Picker": <DatePicker />,
  Slider: <Slider />,
  "Progress Bar": <ProgressBar />,
  Tabs: <Tabs />,
  "Tool Tips": <ToolTips />,
  Menu: <Menu />,
  "Select Menu": <SelectMenu />,
  "Scroll Top": <ScrollTop />,
  "Scroll Down": <ScrollDown />,
  "Horizontal Scroll": <HorizontalScroll />,
  Sortable: <Sortable />,
  Selectable: <Selectable />,
  Resizable: <Resizable />,
  Droppable: <Droppable />,
  Draggable: <Draggable />,
  "Ecommerce Lab": <EcommerceAutomationLab />,
};

const defaultPracticeMeta = {
  difficulty: "Beginner",
  tags: ["selenium", "xpath", "practice"],
  expectedResult: "The user action should update the UI clearly and keep the page stable.",
  actualResultTip: "Observe the visible UI change, console behavior, validation message, or navigation result.",
  locator: "data-testid, id, name, aria-label, role, text",
  selenium: "driver.findElement(By.cssSelector(\"selector\")).click();",
  playwright: "await page.locator('selector').click();",
  testCases: [
    "Verify the default state before interaction.",
    "Perform the primary user action.",
    "Assert the expected UI state or message.",
  ],
  quiz: {
    question: "What should you assert after completing this practice?",
    options: ["Only page color", "Expected UI state and data", "Browser zoom level"],
    answer: "Expected UI state and data",
  },
};

const practiceMeta = {
  "Text Box": {
    difficulty: "Beginner",
    tags: ["forms", "xpath", "input", "validation"],
    locator: "#userName, #userEmail, textarea, button[type='submit']",
    expectedResult: "Submitted text should appear exactly as entered.",
    selenium: "driver.findElement(By.id(\"userName\")).sendKeys(\"Manish\");",
    playwright: "await page.locator('#userName').fill('Manish');",
  },
  "Check Box": {
    difficulty: "Beginner",
    tags: ["checkbox", "xpath", "state"],
    locator: "input[type='checkbox'], label, .checkbox-tree",
    expectedResult: "Selected checkbox state should be visible and consistent.",
  },
  "Radio Button": {
    difficulty: "Beginner",
    tags: ["radio", "forms", "state"],
    locator: "input[type='radio'], label[for]",
    expectedResult: "Only one radio option should be selected at a time.",
  },
  "Web Tables": {
    difficulty: "Intermediate",
    tags: ["tables", "crud", "assertions"],
    locator: "table tbody tr, td, button[aria-label]",
    expectedResult: "Created, edited, searched, and deleted table rows should update correctly.",
    testCases: ["Add a new row and verify it appears.", "Edit a row and verify changed data.", "Delete a row and verify it is removed."],
  },
  Buttons: {
    difficulty: "Beginner",
    tags: ["clicks", "double-click", "right-click"],
    locator: "button, [role='button']",
    expectedResult: "Click, double click, and right click should trigger separate messages.",
  },
  Links: {
    difficulty: "Beginner",
    tags: ["links", "navigation", "http"],
    locator: "a[href], link text",
    expectedResult: "Links should navigate or show the correct status action.",
  },
  "Broken Links": {
    difficulty: "Intermediate",
    tags: ["broken-links", "status-code", "http"],
    locator: "a[href], img[src]",
    expectedResult: "Broken links and broken images should be detectable.",
  },
  "Upload Download": {
    difficulty: "Intermediate",
    tags: ["upload", "download", "files"],
    locator: "input[type='file'], download link",
    expectedResult: "File upload should show selected file and download action should trigger correctly.",
  },
  "Dynamic Properties": {
    difficulty: "Advanced",
    tags: ["waits", "dynamic", "sync"],
    locator: "button:enabled, visible text, dynamic id",
    expectedResult: "Tests should wait for enabled/visible state instead of using fixed sleep.",
    selenium: "new WebDriverWait(driver, Duration.ofSeconds(10)).until(ExpectedConditions.elementToBeClickable(locator));",
    playwright: "await expect(page.locator('button')).toBeEnabled();",
  },
  "Practice Form": {
    difficulty: "Intermediate",
    tags: ["forms", "validation", "submission"],
    locator: "input[name], select, textarea, button[type='submit']",
    expectedResult: "Valid form data should submit and invalid data should show validation.",
  },
  Login: {
    difficulty: "Intermediate",
    tags: ["auth", "forms", "negative-testing"],
    locator: "input[type='email'], input[type='password'], submit button",
    expectedResult: "Valid login should pass and invalid login should show an error.",
  },
  Register: {
    difficulty: "Intermediate",
    tags: ["registration", "forms", "validation"],
    locator: "name, email, password fields",
    expectedResult: "Registration should validate required fields and confirm password.",
  },
  "Browser Windows": {
    difficulty: "Advanced",
    tags: ["windows", "tabs", "handles"],
    locator: "button, window handle",
    expectedResult: "Automation should switch to the new window or tab before asserting content.",
  },
  Alerts: {
    difficulty: "Advanced",
    tags: ["js alerts", "alerts", "waits"],
    locator: "alert trigger button",
    expectedResult: "Alert text should be captured and accepted, dismissed, or filled as required.",
    selenium: "driver.switchTo().alert().accept();",
    playwright: "page.on('dialog', dialog => dialog.accept());",
  },
  Frames: {
    difficulty: "Advanced",
    tags: ["frames", "iframe", "context-switch"],
    locator: "iframe, frameLocator",
    expectedResult: "Automation should switch into the frame before interacting with inner elements.",
    playwright: "await page.frameLocator('iframe').locator('selector').click();",
  },
  "Nested Frames": {
    difficulty: "Advanced",
    tags: ["nested-frames", "iframe", "context-switch"],
    locator: "parent iframe, child iframe",
    expectedResult: "Tests should switch through parent and child frames correctly.",
  },
  "Modal Dialogs": {
    difficulty: "Intermediate",
    tags: ["modal", "dialogs", "overlay"],
    locator: ".modal, dialog, close button",
    expectedResult: "Modal should open, show expected content, and close cleanly.",
  },
  "Auto Complete": {
    difficulty: "Intermediate",
    tags: ["autocomplete", "keyboard", "suggestions"],
    locator: "input[aria-autocomplete], suggestion option",
    expectedResult: "Typing should filter suggestions and selecting should add the chosen value.",
  },
  "Date Picker": {
    difficulty: "Intermediate",
    tags: ["date-picker", "calendar", "forms"],
    locator: "date input, calendar cell",
    expectedResult: "Selected date should populate the input in expected format.",
  },
  "Progress Bar": {
    difficulty: "Intermediate",
    tags: ["waits", "progress", "async"],
    locator: "progressbar, start button",
    expectedResult: "Progress should reach the expected value before assertion.",
  },
  "Tool Tips": {
    difficulty: "Intermediate",
    tags: ["hover", "tooltip", "mouse"],
    locator: "tooltip trigger, role='tooltip'",
    expectedResult: "Tooltip should appear on hover/focus and contain correct text.",
    playwright: "await page.locator('button').hover();",
  },
  Menu: {
    difficulty: "Advanced",
    tags: ["hover", "menu", "nested-menu"],
    locator: "menu item text, submenu item",
    expectedResult: "Hover should reveal submenu items and target item should be clickable.",
  },
  "Select Menu": {
    difficulty: "Intermediate",
    tags: ["select", "dropdown", "forms"],
    locator: "select, combobox, option",
    expectedResult: "Selected option should be visible and submitted value should match.",
  },
  Sortable: {
    difficulty: "Advanced",
    tags: ["drag-drop", "sortable", "mouse"],
    locator: "sortable item",
    expectedResult: "Item order should change after drag and drop.",
  },
  Selectable: {
    difficulty: "Intermediate",
    tags: ["selectable", "state", "mouse"],
    locator: "selectable item",
    expectedResult: "Selected item should show active selected state.",
  },
  Resizable: {
    difficulty: "Advanced",
    tags: ["resize", "mouse", "layout"],
    locator: "resize handle",
    expectedResult: "Element dimensions should change after resizing.",
  },
  Droppable: {
    difficulty: "Advanced",
    tags: ["drag-drop", "droppable", "mouse"],
    locator: "draggable item, drop target",
    expectedResult: "Drop target should show success state after item is dropped.",
  },
  Draggable: {
    difficulty: "Advanced",
    tags: ["drag", "mouse", "position"],
    locator: "draggable element",
    expectedResult: "Element position should change after drag action.",
  },
  "Ecommerce Lab": {
    difficulty: "Advanced",
    tags: ["ecommerce", "checkout", "database", "regression"],
    locator: ".product-card, .checkout-link-btn, input[name='payment-method'], .invoice-box",
    expectedResult: "The full ecommerce journey should complete with one order, one invoice, and correct order history data.",
    selenium: "driver.findElement(By.cssSelector('.product-card')).click();",
    playwright: "await page.locator('.product-card').first().click();",
    testCases: [
      "Open the storefront and verify products load from the database-backed API.",
      "Add one product to cart, update quantity, and verify the summary values.",
      "Select or create an address, complete payment, and verify the invoice and order history.",
    ],
    quiz: {
      question: "What is the highest-value assertion after clicking Pay Now?",
      options: ["Only URL changed", "Order and invoice are saved exactly once", "Button color changed"],
      answer: "Order and invoice are saved exactly once",
    },
  },
};

const assignmentCards = [
  "Write 5 positive and 5 negative test cases for this component.",
  "Create one Selenium locator strategy using id/css/xpath.",
  "Create one Playwright locator strategy using role/text/css.",
  "Document expected result, actual result, and bug severity.",
];

const buggedDemoIdeas = [
  "Missing validation message",
  "Wrong success text",
  "Button enabled too early",
  "Broken locator after UI change",
];

function getStoredProgress() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function getMeta(item) {
  return {
    ...defaultPracticeMeta,
    ...(practiceMeta[item] || {}),
  };
}

export default function AutomationSidebar() {
  const [openMenu, setOpenMenu] = useState("elements");
  const [activeItem, setActiveItem] = useState("Text Box");
  const [searchQuery, setSearchQuery] = useState("");
  const [challengeMode, setChallengeMode] = useState(false);
  const [completedItems, setCompletedItems] = useState(getStoredProgress);
  const [quizAnswer, setQuizAnswer] = useState("");

  const activeMenu = useMemo(
    () => menuData.find((menu) => menu.items.includes(activeItem)) || menuData[0],
    [activeItem]
  );

  const activeMeta = useMemo(() => getMeta(activeItem), [activeItem]);

  const allItems = useMemo(
    () =>
      menuData.flatMap((menu) =>
        menu.items.map((item) => ({
          item,
          menuId: menu.id,
          menuTitle: menu.title,
          ...getMeta(item),
        }))
      ),
    []
  );

  const filteredMenuData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return menuData;
    }

    return menuData
      .map((menu) => ({
        ...menu,
        items: menu.items.filter((item) => {
          const meta = getMeta(item);
          return [item, menu.title, meta.difficulty, ...(meta.tags || [])]
            .join(" ")
            .toLowerCase()
            .includes(query);
        }),
      }))
      .filter((menu) => menu.items.length > 0);
  }, [searchQuery]);

  const completedCount = Object.values(completedItems).filter(Boolean).length;
  const progressPercentage = Math.round((completedCount / allItems.length) * 100);

  const toggleMenu = (menuId) => {
    setOpenMenu(openMenu === menuId ? "" : menuId);
  };

  const handleClick = (item) => {
    setActiveItem(item);
    setQuizAnswer("");
    const matchedMenu = menuData.find((menu) => menu.items.includes(item));
    if (matchedMenu) {
      setOpenMenu(matchedMenu.id);
    }
  };

  const toggleCompleted = () => {
    const nextCompletedItems = {
      ...completedItems,
      [activeItem]: !completedItems[activeItem],
    };

    setCompletedItems(nextCompletedItems);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextCompletedItems));
  };

  const copyLocator = async () => {
    try {
      await navigator.clipboard.writeText(activeMeta.locator);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = activeMeta.locator;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
  };

  return (
    <div className="automation-practice-shell">
      <div className="automation-practice-intro">
        <span className="practice-chip">Automation Practice Lab</span>
        <div>
          <h1>Hands-on UI practice for QA engineers</h1>
          <p>
            Explore realistic controls, forms, widgets, and interaction patterns in a
            cleaner responsive workspace built for practice, debugging, and test design.
          </p>
        </div>
        <div className="practice-progress-card">
          <strong>{progressPercentage}%</strong>
          <span>{completedCount} of {allItems.length} completed</span>
          <div className="practice-progress-track">
            <div style={{ width: `${progressPercentage}%` }}></div>
          </div>
        </div>
      </div>

      <div className="automation-container automation-practice-content">
        <aside className="side-header-wrapper">
          <div className="practice-sidebar-head">
            <h2>Practice Modules</h2>
            <p>Choose a module, then work through the live component on the right.</p>
            <div className="practice-search-box">
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search modules, tags, difficulty..."
                aria-label="Search automation practice modules"
              />
            </div>
          </div>

          <nav className="fusion-main-menu-container" aria-label="Automation practice sections">
            <ul className="fusion-menu">
              {filteredMenuData.map((menu) => (
                <li
                  key={menu.id}
                  className={`menu-item menu-section ${openMenu === menu.id ? "active" : ""}`}
                >
                  <button
                    type="button"
                    className="menu-header"
                    onClick={() => toggleMenu(menu.id)}
                    aria-expanded={openMenu === menu.id}
                  >
                    <span>{menu.title}</span>
                    <span className={`arrow ${openMenu === menu.id ? "rotate" : ""}`}>v</span>
                  </button>

                  <ul className={`submenu ${openMenu === menu.id ? "active" : ""}`}>
                    {menu.items.map((item) => (
                      <li
                        key={item}
                        className={`submenu-item ${activeItem === item ? "active" : ""}`}
                      >
                        <NavLink
                          to={`/${item.replace(/\s+/g, "-").toLowerCase()}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClick(item);
                          }}
                        >
                          <span>{item}</span>
                          <small className={`difficulty-dot ${getMeta(item).difficulty.toLowerCase()}`}>
                            {getMeta(item).difficulty}
                          </small>
                          {completedItems[item] && <strong className="completed-mark">Done</strong>}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            {filteredMenuData.length === 0 && (
              <div className="practice-empty-search">
                No modules found. Try Selenium, forms, waits, alerts, or drag.
              </div>
            )}
          </nav>
        </aside>

        <section className="content">
          <div className="practice-content-header">
            <div className="practice-content-summary">
              <span className="practice-section-label">{activeMenu.title}</span>
              <h2>{activeItem}</h2>
              <p>{activeMenu.description}</p>
              <div className="practice-inline-pills">
                <span>{activeMeta.difficulty}</span>
                {activeMeta.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="practice-header-actions">
              <button
                type="button"
                className={`practice-complete-btn ${completedItems[activeItem] ? "completed" : ""}`}
                onClick={toggleCompleted}
              >
                {completedItems[activeItem] ? "Completed" : "Mark completed"}
              </button>
              <button
                type="button"
                className={`practice-challenge-toggle ${challengeMode ? "active" : ""}`}
                onClick={() => setChallengeMode((currentValue) => !currentValue)}
              >
                {challengeMode ? "Challenge mode on" : "Challenge mode"}
              </button>
            </div>
          </div>

          <div className="practice-component-stage">
            <div className={challengeMode ? "challenge-mode-wrap active" : "challenge-mode-wrap"}>
              {challengeMode && (
                <div className="challenge-banner">
                  <strong>Challenge mode:</strong>
                  <span>Hide hints first. Write your locators and assertions before checking the guide panel.</span>
                </div>
              )}

              {componentMap[activeItem] || (
                <div className="placeholder">
                  Component "{activeItem}" not found. Check your component map keys.
                </div>
              )}
            </div>

            <div className="practice-learning-grid">
              <div className="learning-panel">
                <h3>Expected vs Actual</h3>
                <div className="result-comparison">
                  <div>
                    <strong>Expected result</strong>
                    <p>{activeMeta.expectedResult}</p>
                  </div>
                  <div>
                    <strong>Actual result</strong>
                    <p>{activeMeta.actualResultTip}</p>
                  </div>
                </div>
              </div>

              <div className="learning-panel">
                <h3>Copy locator / inspect help</h3>
                <p className="locator-text">{activeMeta.locator}</p>
                <button type="button" className="copy-locator-btn" onClick={copyLocator}>
                  Copy locator strategy
                </button>
              </div>

              <div className="learning-panel">
                <h3>Try with Selenium</h3>
                <pre><code>{activeMeta.selenium}</code></pre>
              </div>

              <div className="learning-panel">
                <h3>Try with Playwright</h3>
                <pre><code>{activeMeta.playwright}</code></pre>
              </div>

              <div className="learning-panel wide">
                <h3>Test cases for this component</h3>
                <ul>
                  {activeMeta.testCases.map((testCase) => (
                    <li key={testCase}>{testCase}</li>
                  ))}
                </ul>
              </div>

              <div className="learning-panel wide">
                <h3>Automation assignment</h3>
                <div className="assignment-grid">
                  {assignmentCards.map((assignment) => (
                    <span key={assignment}>{assignment}</span>
                  ))}
                </div>
              </div>

              <div className="learning-panel">
                <h3>Bugged demo ideas</h3>
                <ul>
                  {buggedDemoIdeas.map((bug) => (
                    <li key={bug}>{bug}</li>
                  ))}
                </ul>
              </div>

              <div className="learning-panel">
                <h3>Mini quiz</h3>
                <p>{activeMeta.quiz.question}</p>
                <div className="quiz-options">
                  {activeMeta.quiz.options.map((option) => (
                    <button
                      type="button"
                      key={option}
                      className={quizAnswer === option ? "selected" : ""}
                      onClick={() => setQuizAnswer(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {quizAnswer && (
                  <p className={quizAnswer === activeMeta.quiz.answer ? "quiz-result correct" : "quiz-result wrong"}>
                    {quizAnswer === activeMeta.quiz.answer
                      ? "Correct. Focus your automation on reliable state assertions."
                      : "Try again. Automation should verify expected state and data."}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
